// Shared parser for NetSuite's "required field" rejections.
//
// The same rules exist in Python as NetSuiteService.classify_probe_error /
// _parse_required_field_labels. Both sides read the SAME error strings from
// the SAME account, so two independent copies would eventually disagree about
// what a rejection means — and the one place that matters is exactly where a
// wrong answer is invisible: telling an admin "nothing is required" because a
// message could not be parsed.
//
// Previously the wizard carried its own private copy of the label regex
// (NsLineLinkWizard.jsx). This is that copy, lifted out and given the
// classification the probe needs, so the wizard and the probe cannot drift.

/** Field LABELS named by a "please enter value(s) for: A, B." rejection. */
export function parseMissingFieldLabels(message) {
  if (!message) return []
  const match = /values?\(?s?\)?\s+for:\s*(.+?)\.?\s*$/i.exec(String(message))
  if (!match) return []
  return match[1].split(',').map(s => s.trim()).filter(Boolean)
}

/** Map rejection labels back onto catalog fields, by label or key. */
export function resolveFieldsByLabel(catalog, labels) {
  const resolved = []
  const seen = new Set()
  for (const label of labels) {
    const norm = String(label).trim().toLowerCase()
    const field = (catalog || []).find(
      f => !seen.has(f.key) &&
           (String(f.label || '').toLowerCase() === norm ||
            String(f.key || '').toLowerCase() === norm)
    )
    if (field) {
      resolved.push(field)
      seen.add(field.key)
    }
  }
  return resolved
}

/**
 * What a rejection actually means.
 *
 * Four outcomes, kept distinct on purpose:
 *   missing_required — NetSuite named fields it demands.
 *   invalid_value    — a value WE supplied was wrong. Says nothing about
 *                      required-ness; treating it as such would record a
 *                      false positive.
 *   forbidden        — the role may not create this record type at all.
 *   unparsed         — we could not tell. Must never be rendered as
 *                      "no required fields".
 */
export function classifyProbeError(message) {
  const text = String(message || '')
  if (/(do not have permission|insufficient permission|not permitted)/i.test(text)) {
    return { kind: 'forbidden', labels: [], invalidField: null }
  }
  const invalid = /invalid field value\s+(.+?)\s+for the following field:\s*([\w.]+)/i.exec(text)
  if (invalid) {
    return { kind: 'invalid_value', labels: [], invalidField: invalid[2] }
  }
  const labels = parseMissingFieldLabels(text)
  if (labels.length) {
    return { kind: 'missing_required', labels, invalidField: null }
  }
  return { kind: 'unparsed', labels: [], invalidField: null }
}

/** Human summary of a probe result, phrased so an inconclusive run is never
 *  mistaken for a clean bill of health. */
export function describeProbeStatus(status) {
  switch (status) {
    case 'ok':
      return 'NetSuite accepted a test record — these are the fields it actually requires.'
    case 'inconclusive':
      return 'Inconclusive — NetSuite returned something we could not interpret. ' +
             'Nothing was confirmed; treat the required fields below as incomplete.'
    case 'exhausted':
      return 'Stopped after the attempt limit without NetSuite accepting a record. ' +
             'The fields found so far are real, but the list may be incomplete.'
    case 'forbidden':
      return 'This NetSuite role is not permitted to create this record type, so nothing could be tested.'
    case 'skipped':
      return 'Test records are disabled for this account. Required fields come from ' +
             'NetSuite metadata only, which is known to be incomplete.'
    case 'never':
      return 'Not tested yet. Required fields come from NetSuite metadata only, ' +
             'which is known to be incomplete.'
    default:
      return 'The test did not complete.'
  }
}
